import React, { Suspense } from 'react';
import { match } from 'ts-pattern';
import type { CMSTextAdvanced as CMSTextAdvancedType } from '../schemas';
import { CMSText } from '../cms-text';
import { CMSCard } from '../cms-card';
import { TrustpilotSection } from '@/components/trustpilot-section';
import { CMSPlanCardsWrapper } from '../cms-plan-cards-wrapper';
import { cn } from '@/lib/utils';
import { TrustPilotWidget } from '@/components/trustpilot-wiget';
import { CMSCTAButton } from '../cms-cta-button';
import { CMSQueueCount } from '../cms-queue-count';
import { CMSInlineTextImage } from '../cms-inline-text-image';
import { CMSPlaceholder } from '../cms-placeholder';
import { CMSForm } from '../cms-form';

const LazyAddressSearch = React.lazy(
  () => import('@/components/address-search/index')
);
const LazyCMSVideo = React.lazy(() => import('../cms-video'));

interface CMSTextAdvancedProps {
  textAdvancedData: CMSTextAdvancedType;
  className?: string;
  isFromBanner?: boolean;
  style?: React.CSSProperties;
}
const CMSTextAdvanced = ({
  className,
  textAdvancedData,
  isFromBanner = false,
  style,
}: CMSTextAdvancedProps) => {
  return (
    <div
      style={style}
      className={cn(
        //add flex styles if CMSTextAdvanced has CMSHorizontalCard or CMSPlanCardBroadband
        `[&:has([data-component="CMSHorizontalCard"],[data-component="CMSPlanCardBroadband"])]:flex 
         [&:has([data-component="CMSHorizontalCard"],[data-component="CMSPlanCardBroadband"])]:flex-col 
         [&:has([data-component="CMSHorizontalCard"],[data-component="CMSPlanCardBroadband"])]:gap-2 
         lg:[&:has([data-component="CMSHorizontalCard"],[data-component="CMSPlanCardBroadband"])]:gap-4`,

        //target p tag title (e.g "The plan", "The features") if CMSTextAdvanced has CMSHorizontalCard or CMSPlanCardBroadband
        `[&:has([data-component="CMSHorizontalCard"],[data-component="CMSPlanCardBroadband"])_>_div_>_div_>_p]:!text-base 
         lg:[&:has([data-component="CMSHorizontalCard"],[data-component="CMSPlanCardBroadband"])_>_div_>_div_>_p]:!text-xl`,

        // Remove margin bottom for p tag title p tag title (e.g "The plan", "The features")
        `[&:has([data-component="CMSHorizontalCard"],[data-component="CMSPlanCardBroadband"])_>_div_>_div_>_p]:!mb-0`,

        //Apply w-full if CMSTextAdvanced has CMSHorizontalCard or CMSPlanCardBroadband
        '[&:has([data-component="CMSHorizontalCard"],[data-component="CMSPlanCardBroadband"])]:w-full',
        className
      )}
      data-component="CMSTextAdvanced"
    >
      {textAdvancedData.items.map((item, index) =>
        match(item)
          .with({ type: 'text' }, (textNode) => (
            <CMSText key={`${textNode.id}-${index}`} textData={textNode} />
          ))
          .with({ type: 'card' }, (cardData) => (
            <CMSCard key={`${cardData.id}-${index}`} cardData={cardData} />
          ))
          .with({ type: 'address_search' }, () => (
            <React.Suspense key={`${item.id}-${index}`} fallback={null}>
              <LazyAddressSearch
                isFromBanner={isFromBanner}
                key={`${item.id}-${index}`}
              />
            </React.Suspense>
          ))
          .with({ type: 'trustpilot_carousel' }, () => (
            <TrustpilotSection key={`${item.id}-${index}`} />
          ))
          .with({ type: 'trustpilot_widget' }, () => (
            <TrustPilotWidget key={`${item.id}-${index}`} />
          ))
          .with({ type: 'plan_cards' }, (planCardsData) => (
            <CMSPlanCardsWrapper
              className="h-full"
              key={`${planCardsData.id}-${index}`}
              planCardsData={planCardsData}
            />
          ))
          .with({ type: 'cta' }, (data) => {
            return (
              <CMSCTAButton
                key={`${data.id}-${index}`}
                className={cn('mt-2 md:mt-4')}
                ctaData={data}
              />
            );
          })
          .with({ type: 'video' } as const, (data) => (
            <Suspense fallback={null} key={`${data.id}-${index}`}>
              <LazyCMSVideo data={data} className={cn('rounded-[15px]')} />
            </Suspense>
          ))
          .with({ type: 'queue_count' } as const, (data) => (
            <CMSQueueCount key={`${data.id}-${index}`} queueCountData={data} />
          ))
          .with({ type: 'inline_text_image' } as const, (data) => (
            <CMSInlineTextImage
              key={`${data.id}-${index}`}
              inlineTextImageData={data}
            />
          ))
          .with({ type: 'placeholder' } as const, (data) => (
            <CMSPlaceholder
              key={`${data.id}-${index}`}
              placeholderData={data}
            />
          ))
          .with({ type: 'form' } as const, (formData) => (
            <CMSForm formData={formData} key={`${formData.id}-${index}`} />
          ))
          .otherwise(() => null)
      )}
    </div>
  );
};
export { CMSTextAdvanced };
